import React, { useState } from 'react';
import { get } from 'lodash/fp';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose, withProps } from 'recompose';
import { reduxForm } from 'redux-form';

import { SelectField, TextAreaField } from 'components/form/field';
import dataProvider from 'components/dataProvider';
import { filterRecommendations } from 'common/utils/recommendations';
import { setModal } from 'actions';
import SuccessModalForPreco from '../../../SuccessModalForPreco';
import { withSession } from '../../../sessions';
import TargetField from '../../approval/TargetField';
import RipFormFields from '../../FormFields';
import SendButton from '../../../SendButton';
import Download from '../../Download';
import Recommendations from './Recommendations';
import Approved from './Approved';
import getPrecoChoiceParameters from '../../../../utils/getPrecoChoiceParameters';
import './Form.scss';

const maySelectRecipient = (session) => get('settings.canSelectRecommendationRecipient', session)
    && get('entity.managerId', session)
    && get('entity.caseManagerId', session);

const getCommentFieldName = (isCif, target) => {
    if (!isCif) {
        return 'recommendationComments';
    }
    if ('clientAndPartner' === target) {
        return 'recommendationCommentsCIFClientAndPartner';
    }

    return 'recommendationCommentsCIF';
};

const FormInner = ({ rip, session, ...props }) => {
    const { instanceTheseis } = session;
    const [isCif, setIsCif] = useState(false);
    const [target, setTarget] = useState('client');

    return (
        <RipFormFields {...props}>
            <div className="recommendation">
                <Download rip={rip} isInstanceTheseis={instanceTheseis} />
                {(rip.recommendations || rip.partner) && <Approved recommendations={'client' === target ? rip.recommendations : rip.partner && rip.partner.recommendations} />}
                <TargetField rip={rip} />
                <Recommendations rip={rip} setIsCif={setIsCif} isCif={isCif} setTarget={setTarget} required />
                <TextAreaField
                    name={getCommentFieldName(isCif, target)}
                    title={`Message pour mon ${isCif ? "chargé d'affaires" : 'validateur'}`}
                />
                {maySelectRecipient(session) ? (
                    <SelectField name="recommendationRecipient" title="Destinataire" required>
                        <option />
                        <option value="manager">Mon manager</option>
                        <option value="caseManager">Mon charg&eacute; d&apos;affaires</option>
                        <option value="both">Mon manager et mon charg&eacute; d&apos;affaires</option>
                    </SelectField>
                ) : null}
                <SendButton />
            </div>
        </RipFormFields>
    );
};

FormInner.propTypes = {
    rip: PropTypes.shape({
        recommendations: PropTypes.shape({}),
        partner: PropTypes.shape({
            recommendations: PropTypes.shape({}),
        }),
    }).isRequired,
    session: PropTypes.shape({
        instanceTheseis: PropTypes.bool,
        needApprovalOnCIFDocument: PropTypes.bool,
    }).isRequired,
};

const Form = compose(
    dataProvider({
        id: ({ id }) => id,
        type: 'rip',
        wait: true,
    }),
    withProps(({ rip }) => ({
        initialValues: ({
            target: 'client',
            recommendations: filterRecommendations(rip.recommendations),
            // Les demandes CIF en cours ne sont pas pré-affichées : l'affichage se fait au clic sur
            // un produit (un seul à la fois). Les données existantes sont réinjectées à l'ouverture
            // du bloc via les defaultValues de RecommendationLists.
            partner: {
                recommendations: filterRecommendations(get('partner.recommendations', rip)),
                recommendationsCIF: [],
            },
            recommendationComments: rip.recommendationComments,
            recommendationsCIF: [],
            recommendationCommentsCIF: rip.recommendationCommentsCIF,
            recommendationsCIFClientAndPartner: [],
            recommendationCommentsCIFClientAndPartner: rip.recommendationCommentsCIFClientAndPartner,
            choicePreco: getPrecoChoiceParameters(),
            recommendationRecipient: 'caseManager',
        }),
    })),
    reduxForm({
        form: 'rip-recommendation',
        id: ({ id }) => id,
        type: 'rip',
        onSubmitSuccess: (result, dispatch, { values: { choicePreco } }) => {
            const isCif = 'cif' === choicePreco;

            return (dispatch(setModal(() => (<SuccessModalForPreco isCif={isCif} />), false)));
        },
    }),
    connect(
        null,
        (dispatch, { handleSubmit }) => ({
            handleSubmit: (event) => {
                event.preventDefault();
                handleSubmit(event);
            },
        }),
    ),
    withSession,
)(FormInner);

export default Form;
